import { Avatar, Box, ListItem, ListItemAvatar, ListItemText } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import EllipsisText from '../../../../components/EllipsisText';
import { stringAvatar } from '../../../../utils/commonUtils';
import { SHARE_PERMISSION_OPTIONS_WITH_REVOKE } from '../../../constants';
import type { ShareAccessDisplayItem, ShareAction } from '../../../types';
import { useInternalShareSettings } from '../../hooks/useInternalShareSettings';
import ShareActionFormField from '../dataInput/ShareActionFormField';

type ShareActorListItemProps = {
    shareAccessDisplayItem: ShareAccessDisplayItem;
};

export default function ShareActorListItem({ shareAccessDisplayItem }: ShareActorListItemProps) {
    const { actor, isOwner, action, hasChanged, source } = shareAccessDisplayItem;

    const { shareSettings, updateShareSettingsState } = useInternalShareSettings();
    const handleActorActionChange = useCallback(
        (newActionValue: ShareAction) => {
            if (source === 'departments') {
                const updatedSharedWithDepartments = shareSettings.sharedWithDepartments.map((shareAccess) =>
                    shareAccess.actor.id === actor.id ? { ...shareAccess, action: newActionValue } : shareAccess
                );

                updateShareSettingsState({
                    sharedWithDepartments: updatedSharedWithDepartments
                });

                return;
            }

            const updatedSharedWith = shareSettings.sharedWith.map((shareAccess) =>
                shareAccess.actor.id === actor.id ? { ...shareAccess, action: newActionValue } : shareAccess
            );

            updateShareSettingsState({
                sharedWith: updatedSharedWith
            });
        },
        [actor.id, shareSettings.sharedWith, shareSettings.sharedWithDepartments, source, updateShareSettingsState]
    );

    const avatarProps = useMemo(() => stringAvatar(actor.name), [actor.name]);

    return (
        <ListItem alignItems="center" disablePadding sx={{ py: 0.75 }}>
            <ListItemAvatar sx={{ flexShrink: 0, minWidth: 40 }}>
                <Avatar {...avatarProps} sx={{ marginRight: 1, ...avatarProps.sx }} />
            </ListItemAvatar>
            <ListItemText
                primary={<EllipsisText text={actor.name} />}
                secondary={actor.email ? <EllipsisText text={actor.email} sx={{ color: 'text.disabled' }} /> : null}
                slotProps={{
                    primary: {
                        component: 'div',
                        sx: hasChanged ? { fontStyle: 'italic' } : undefined
                    },
                    secondary: {
                        component: 'div',
                        sx: hasChanged ? { fontStyle: 'italic' } : undefined
                    }
                }}
            />
            <Box marginLeft={1}>
                <ShareActionFormField
                    value={action}
                    onChange={handleActorActionChange}
                    disabled={isOwner}
                    options={SHARE_PERMISSION_OPTIONS_WITH_REVOKE}
                    hasChanged={hasChanged}
                />
            </Box>
        </ListItem>
    );
}
